import Intercom from '@intercom/messenger-js-sdk';
import { enc, HmacSHA256 } from 'crypto-js';
import React, { FC, ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useCookies } from '../../context/cookies.store';
import { HeaderContextProvider } from '../../context/header-context';
import { useLocaleContext } from '../../context/locale-context';
import { NotificationsProvider } from '../../context/notifications-context';
import { AnalyticHooks, useModalState, User } from '../../hooks/index';
import { eventSelectedTabStore } from '../../pages/events/desktop/history/history.store';
import InviteModal from '../../pages/my-companies/invite-modal';
import { localeStorage } from '../../storage/local/index';
import { paginationStorage } from '../../storage/local/index';
import { prevPageStorage } from '../../storage/local/index';
import ChatButton from '../chat-button/chat-button';
import { CookiePolicyModal, CookieSettingsModal } from '../cookie-policy/index';
import { DownloadDocumentsModal } from '../downlaod-documents/index';
import HeaderLayout from '../header-layout/index';
import SideBarLayout from '../side-bar-layout/index';

export type AppLayoutProps = {
  children: ReactNode;
  className?: string;
};
const secretKey = process.env.REACT_APP_SECRET_INTERCOM_KEY;

const useRegisterPageChange = () => {
  const { pathname } = useLocation();
  const prefPage = useRef(prevPageStorage.get());

  if (prefPage.current !== pathname) {
    prefPage.current = pathname;
    prevPageStorage.set(pathname);
    paginationStorage.clearAll();
  }
};

const AppLayout: FC<AppLayoutProps> = ({ children, className }) => {
  AnalyticHooks.usePageView();
  AnalyticHooks.useScrollDepth();
  AnalyticHooks.useTrackTimeOnPage();

  useRegisterPageChange();

  const { user } = User.useUser();
  const { setLocale } = useLocaleContext();

  const { toggleCookieModalOpenState, isOpenCookieModal } = useCookies();

  const {
    isOpen: isOpenCookieSettingsModal,
    handleOpenModal: handleOpenCookieSettingsModal,
    handleCloseModal: handleCloseCookieSettingsModal,
  } = useModalState();

  useEffect(() => {
    if (!user || !secretKey) return;
    const userHash = HmacSHA256(user.id, secretKey).toString(enc.Hex);

    Intercom({
      app_id: process.env.REACT_APP_INTERCOM_ID || '',
      user_id: user.id,
      name: user.fullName,
      email: user.email,
      created_at: new Date(user.createdAt).getTime(),
      user_hash: userHash,
      hide_default_launcher: true,
    });
  }, [user]);

  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname !== '/events') eventSelectedTabStore.getState().reset();
  }, [pathname]);

  useEffect(() => {
    if (!user) return;
    localeStorage.set(user.language);
    setLocale(user.language);
  }, [setLocale, user]);

  return (
    <>
      <NotificationsProvider>
        <SideBarLayout>
          <HeaderContextProvider>
            <ChatButton />
            <DownloadDocumentsModal />
            <HeaderLayout className={className}>{children}</HeaderLayout>
          </HeaderContextProvider>
        </SideBarLayout>
        <InviteModal />
        <CookiePolicyModal
          handleCloseModal={() => toggleCookieModalOpenState(false)}
          handleOpenSettingsModal={handleOpenCookieSettingsModal}
          isOpenModal={isOpenCookieModal}
        />
        <CookieSettingsModal
          handleCloseModal={handleCloseCookieSettingsModal}
          handleOpenCookieModal={() => toggleCookieModalOpenState(true)}
          isOpenModal={isOpenCookieSettingsModal}
        />
      </NotificationsProvider>
    </>
  );
};

AppLayout.displayName = 'AppLayout';

export default AppLayout;
